import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  List,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd'
import type { TabsProps } from 'antd'
import {
  AppstoreOutlined,
  LockOutlined,
  PlusOutlined,
  SafetyOutlined,
  SearchOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/ru'
import { useQueryClient } from '@tanstack/react-query'

import { useAuthStore } from '@/store/auth'
import { useAccessGroups, useAddMember, useEffectiveRlsRows, useRemoveMember, useUserRlsBindings } from '@/hooks/useAccessGroups'
import {
  useDeleteUserDashboardDeny,
  useDeleteUserDashboardGrant,
  useOrganizations,
  useUserAccessGroups,
  useUserDashboardAccess,
  useUsers,
} from '@/hooks/useUsers'
import type { AccessGroupResponse } from '@/api/accessGroups'
import type { UserDashboardAccessExceptionCreatePayload } from '@/api/users'
import * as usersApi from '@/api/users'
import { ROLE_COLORS, ROLE_LABELS } from '@/utils/userRoles'
import { parseBooleanParam, setQueryParam } from '@/utils/searchParams'
import DatalensEntryPicker, { type DatalensEntryMarker, type DatalensEntrySelection } from '@/components/DatalensEntryPicker'
import CreateUserWizard from './CreateUserWizard'
import UserIdentityTab from './UserIdentityTab'
import UserRlsTab from './UserRlsTab'

dayjs.extend(relativeTime)
dayjs.locale('ru')

const { Title, Text, Paragraph } = Typography

type GroupSummary = Pick<
  AccessGroupResponse,
  'id' | 'name' | 'zitadel_role_key' | 'description' | 'org_id' | 'is_active'
>

function getGroupAccessMode(roleKey: string): { label: string; color: string } {
  if (roleKey.endsWith('top-mngmnt') || roleKey.endsWith('top-mgmnt')) {
    return { label: 'Полный доступ к данным', color: 'gold' }
  }

  const match = roleKey.match(/access-(fil|podr|srv|otd)$/)
  if (match) {
    const labels: Record<string, string> = {
      fil: 'RLS: филиалы',
      podr: 'RLS: подразделения',
      srv: 'RLS: службы',
      otd: 'RLS: отделения',
    }
    return { label: labels[match[1]] ?? 'RLS', color: 'blue' }
  }

  return { label: 'Спец. доступ', color: 'default' }
}

function getGroupScope(group: GroupSummary): { label: string; color: string } {
  if (group.org_id) {
    return { label: 'Орг-группа', color: 'green' }
  }
  return { label: 'Региональная', color: 'purple' }
}

function getInheritedScopeMeta(scope: string): { label: string; color: string } {
  if (scope === 'multi_group_cross_org') {
    return { label: 'унаследовано из разных ЛПУ', color: 'volcano' }
  }
  if (scope === 'multi_group_same_org') {
    return { label: 'унаследовано из нескольких групп', color: 'orange' }
  }
  return { label: 'унаследовано из одной группы', color: 'green' }
}

function getOverrideRiskMeta(risk: string): { label: string; color: string } {
  if (risk === 'high') {
    return { label: 'риск override высокий', color: 'volcano' }
  }
  if (risk === 'medium') {
    return { label: 'риск override средний', color: 'orange' }
  }
  return { label: 'риск override низкий', color: 'green' }
}

function getExceptionTypeMeta(exceptionType: string): { label: string; color: string } {
  if (exceptionType === 'deny') {
    return { label: 'персонально скрыт', color: 'red' }
  }
  return { label: 'персонально открыт', color: 'cyan' }
}

function getProjectedEffectMeta(effect: string): { label: string; color: string } {
  if (effect === 'would_add_visible') {
    return { label: 'добавит видимость', color: 'green' }
  }
  if (effect === 'would_remove_visible') {
    return { label: 'скроет доступ', color: 'volcano' }
  }
  if (effect === 'conflict') {
    return { label: 'конфликт', color: 'purple' }
  }
  if (effect === 'no_effect_already_visible') {
    return { label: 'без эффекта: уже виден', color: 'blue' }
  }
  return { label: 'без эффекта: уже скрыт', color: 'default' }
}

function getProjectedVisibilityMeta(visibility: string): { label: string; color: string } {
  if (visibility === 'would_be_hidden') {
    return { label: 'итог: скрыт', color: 'volcano' }
  }
  if (visibility === 'conflict_keeps_visible') {
    return { label: 'конфликт: остается виден', color: 'purple' }
  }
  if (visibility === 'visible_with_redundant_grant') {
    return { label: 'итог: виден, разрешение лишнее', color: 'blue' }
  }
  return { label: 'итог: виден', color: 'green' }
}

function formatLastLogin(value: string | null): string {
  if (!value) {
    return 'Не входил'
  }
  return `${dayjs(value).format('DD.MM.YYYY HH:mm')} (${dayjs(value).fromNow()})`
}

function getApiErrorDetail(error: unknown): string | undefined {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined
  }

  const response = (error as { response?: unknown }).response
  if (!response || typeof response !== 'object' || !('data' in response)) {
    return undefined
  }

  const data = (response as { data?: unknown }).data
  if (!data || typeof data !== 'object' || !('detail' in data)) {
    return undefined
  }

  const detail = (data as { detail?: unknown }).detail
  return typeof detail === 'string' ? detail : undefined
}

export default function AdminAccessWorkspacePage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const authUser = useAuthStore((s) => s.user)
  const isSuperadmin = authUser?.role === 'superadmin'
  const { token: designToken } = theme.useToken()

  const [search, setSearch] = useState(() => searchParams.get('q') ?? '')
  const [orgFilter, setOrgFilter] = useState<string | undefined>(() =>
    isSuperadmin ? (searchParams.get('org') ?? undefined) : (authUser?.orgId ?? undefined)
  )
  const [roleFilter, setRoleFilter] = useState<string | undefined>(() => searchParams.get('role') ?? undefined)
  const [statusFilter, setStatusFilter] = useState<boolean | undefined>(() => parseBooleanParam(searchParams.get('status')) ?? true)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(() => searchParams.get('user'))
  const [compareUserId, setCompareUserId] = useState<string | null>(() => searchParams.get('compare'))
  const [addGroupId, setAddGroupId] = useState<string | undefined>(undefined)
  const [dashboardSearch, setDashboardSearch] = useState(() => searchParams.get('dashq') ?? '')
  const [wizardOpen, setWizardOpen] = useState(false)
  const [exceptionModalOpen, setExceptionModalOpen] = useState(false)
  const [exceptionMode, setExceptionMode] = useState<'grant' | 'deny'>('grant')
  const [exceptionForm] = Form.useForm<Pick<UserDashboardAccessExceptionCreatePayload, 'note'>>()
  const [selectedExceptionEntries, setSelectedExceptionEntries] = useState<DatalensEntrySelection[]>([])
  const [applyingDashboardAccess, setApplyingDashboardAccess] = useState(false)

  const deferredSearch = useDeferredValue(search)

  const { data: organizations } = useOrganizations()
  const { data: usersData, isLoading: usersLoading } = useUsers({
    page: 1,
    page_size: 200,
    search: deferredSearch || undefined,
    role: roleFilter,
    is_active: statusFilter,
    org_id: isSuperadmin ? orgFilter : (authUser?.orgId ?? undefined),
  })

  const users = useMemo(() => usersData?.items || [], [usersData?.items])
  const selectedUser = users.find((user) => user.id === selectedUserId) ?? null

  useEffect(() => {
    if (usersLoading) {
      return
    }

    if (users.length === 0) {
      setSelectedUserId(null)
      return
    }

    const selectedStillExists = selectedUserId && users.some((user) => user.id === selectedUserId)
    if (!selectedStillExists) {
      setSelectedUserId(users[0].id)
    }
  }, [selectedUserId, users, usersLoading])

  useEffect(() => {
    setAddGroupId(undefined)
  }, [selectedUserId])

  useEffect(() => {
    if (compareUserId && compareUserId === selectedUserId) {
      setCompareUserId(null)
    }
  }, [compareUserId, selectedUserId])

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    setQueryParam(next, 'q', search || null)
    setQueryParam(next, 'org', isSuperadmin ? (orgFilter ?? null) : null)
    setQueryParam(next, 'role', roleFilter ?? null)
    setQueryParam(
      next,
      'status',
      statusFilter === undefined ? null : (statusFilter ? 'active' : 'inactive'),
    )
    setQueryParam(next, 'user', selectedUserId)
    setQueryParam(next, 'compare', compareUserId)
    setQueryParam(next, 'dashq', dashboardSearch || null)
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [
    compareUserId,
    dashboardSearch,
    isSuperadmin,
    orgFilter,
    roleFilter,
    search,
    searchParams,
    selectedUserId,
    setSearchParams,
    statusFilter,
  ])

  const canLoadSelectedUserRls = Boolean(selectedUserId && selectedUser?.org_code)

  const { data: userGroups, isLoading: userGroupsLoading } = useUserAccessGroups(selectedUserId)
  const { data: dashboardAccess, isLoading: dashboardAccessLoading } = useUserDashboardAccess(selectedUserId)
  const { data: compareDashboardAccess, isLoading: compareDashboardAccessLoading } = useUserDashboardAccess(compareUserId)
  const { data: allGroups, isLoading: allGroupsLoading } = useAccessGroups()
  const { data: userRlsBindings } = useUserRlsBindings(selectedUserId ?? '', canLoadSelectedUserRls)
  const { data: effectiveRows } = useEffectiveRlsRows(selectedUserId ?? '', canLoadSelectedUserRls)

  const addMemberMutation = useAddMember()
  const removeMemberMutation = useRemoveMember()
  const deleteGrantMutation = useDeleteUserDashboardGrant()
  const deleteDenyMutation = useDeleteUserDashboardDeny()

  const availableGroups = useMemo(() => {
    if (!selectedUser || !allGroups) {
      return []
    }

    const currentIds = new Set((userGroups || []).map((group) => group.id))
    return allGroups.filter((group) => {
      if (!group.is_active || currentIds.has(group.id)) {
        return false
      }

      if (!selectedUser.org_id) {
        return group.org_id === null
      }

      return group.org_id === null || group.org_id === selectedUser.org_id
    })
  }, [allGroups, selectedUser, userGroups])

  const orgOptions = useMemo(
    () =>
      (organizations || []).map((org) => ({
        value: org.id,
        label: `${org.code} — ${org.name}`,
      })),
    [organizations]
  )
  const workspaceOrgId = isSuperadmin ? orgFilter : (authUser?.orgId ?? undefined)
  const workspaceOrgLabel = workspaceOrgId
    ? orgOptions.find((org) => org.value === workspaceOrgId)?.label || workspaceOrgId
    : null
  const biCenterUrl = workspaceOrgId ? `/admin/bi-workspace?org=${workspaceOrgId}` : '/admin/bi-workspace'

  const currentGroupSummary = useMemo(
    () => (userGroups || []).map((group) => ({
      ...group,
      accessMode: getGroupAccessMode(group.zitadel_role_key),
      scope: getGroupScope(group),
    })),
    [userGroups]
  )
  const effectiveDashboardEntries = dashboardAccess?.items || []
  const filteredEffectiveDashboardEntries = useMemo(() => {
    if (!dashboardSearch) {
      return effectiveDashboardEntries
    }
    const needle = dashboardSearch.toLowerCase()
    return effectiveDashboardEntries.filter((entry) =>
      `${entry.label} ${entry.datalens_entry_id} ${entry.source_groups.map((group) => group.name).join(' ')}`.toLowerCase().includes(needle)
    )
  }, [dashboardSearch, effectiveDashboardEntries])
  const compareEffectiveDashboardEntries = compareDashboardAccess?.items || []
  const dashboardAccessSummary = dashboardAccess?.summary
  const directExceptions = dashboardAccess?.exceptions || []
  const directExceptionSummary = dashboardAccess?.exception_summary
  const projectionSummary = dashboardAccess?.projection_summary
  const directGrantExceptions = useMemo(
    () => directExceptions.filter((exception) => exception.exception_type === 'grant'),
    [directExceptions]
  )
  const directDenyExceptions = useMemo(
    () => directExceptions.filter((exception) => exception.exception_type === 'deny'),
    [directExceptions]
  )
  const directGrantIds = useMemo(
    () => new Set(directGrantExceptions.map((exception) => exception.datalens_entry_id)),
    [directGrantExceptions]
  )
  const directDenyIds = useMemo(
    () => new Set(directDenyExceptions.map((exception) => exception.datalens_entry_id)),
    [directDenyExceptions]
  )
  const exceptionMarkersByEntryId = useMemo(() => {
    const markers: Record<string, DatalensEntryMarker[]> = {}
    const pushMarker = (entryId: string, marker: DatalensEntryMarker) => {
      markers[entryId] = [...(markers[entryId] || []), marker]
    }

    effectiveDashboardEntries.forEach((entry) => {
      pushMarker(entry.datalens_entry_id, { label: 'видит по группе', color: 'green' })
    })
    directGrantExceptions.forEach((exception) => {
      pushMarker(exception.datalens_entry_id, { label: 'персонально открыт', color: 'cyan' })
    })
    directDenyExceptions.forEach((exception) => {
      pushMarker(exception.datalens_entry_id, { label: 'персонально скрыт', color: 'red' })
    })

    return markers
  }, [directDenyExceptions, directGrantExceptions, effectiveDashboardEntries])
  const directExceptionsByEntryId = useMemo(() => {
    const mapping = new Map<string, typeof directExceptions>()
    directExceptions.forEach((exception) => {
      const bucket = mapping.get(exception.datalens_entry_id)
      if (bucket) {
        bucket.push(exception)
        return
      }
      mapping.set(exception.datalens_entry_id, [exception])
    })
    return mapping
  }, [directExceptions])
  const compareUser = users.find((user) => user.id === compareUserId) ?? null
  const compareUserOptions = useMemo(
    () =>
      users
        .filter((user) => user.id !== selectedUserId)
        .map((user) => ({
          value: user.id,
          label: `${user.full_name} · ${user.email}`,
        })),
    [selectedUserId, users]
  )
  const dashboardAccessDiff = useMemo(() => {
    const compareIds = new Set(compareEffectiveDashboardEntries.map((entry) => entry.datalens_entry_id))
    const selectedIds = new Set(effectiveDashboardEntries.map((entry) => entry.datalens_entry_id))
    return {
      selectedOnly: effectiveDashboardEntries.filter((entry) => !compareIds.has(entry.datalens_entry_id)),
      compareOnly: compareEffectiveDashboardEntries.filter((entry) => !selectedIds.has(entry.datalens_entry_id)),
    }
  }, [compareEffectiveDashboardEntries, effectiveDashboardEntries])
  const accessMutationBusy =
    applyingDashboardAccess ||
    addMemberMutation.isPending ||
    removeMemberMutation.isPending ||
    deleteGrantMutation.isPending ||
    deleteDenyMutation.isPending
  const selectedExceptionEntryIds = useMemo(
    () => selectedExceptionEntries.map((entry) => entry.datalens_entry_id),
    [selectedExceptionEntries]
  )
  const disabledExceptionEntryIds = exceptionMode === 'grant' ? directGrantIds : directDenyIds

  const handleAddGroup = async () => {
    if (!selectedUserId || !addGroupId) {
      return
    }

    await addMemberMutation.mutateAsync({
      groupId: addGroupId,
      userId: selectedUserId,
    })
    setAddGroupId(undefined)
    await queryClient.invalidateQueries({ queryKey: ['user-access-groups', selectedUserId] })
    await queryClient.invalidateQueries({ queryKey: ['user-dashboard-access', selectedUserId] })
  }

  const handleRemoveGroup = async (groupId: string) => {
    if (!selectedUserId) {
      return
    }

    await removeMemberMutation.mutateAsync({
      groupId,
      userId: selectedUserId,
    })
    await queryClient.invalidateQueries({ queryKey: ['user-access-groups', selectedUserId] })
    await queryClient.invalidateQueries({ queryKey: ['user-dashboard-access', selectedUserId] })
  }

  const openExceptionModal = (mode: 'grant' | 'deny') => {
    setExceptionMode(mode)
    setExceptionModalOpen(true)
    setSelectedExceptionEntries([])
    exceptionForm.resetFields()
  }

  const handleCreateException = async () => {
    if (!selectedUserId) {
      return
    }

    if (selectedExceptionEntries.length === 0) {
      return
    }
    const values = await exceptionForm.validateFields()
    const createException =
      exceptionMode === 'grant'
        ? usersApi.createUserDashboardGrant
        : usersApi.createUserDashboardDeny
    const entries = selectedExceptionEntries.filter(
      (entry) => !disabledExceptionEntryIds.has(entry.datalens_entry_id)
    )

    if (entries.length === 0) {
      message.info('Выбранные дашборды уже есть в этом типе исключений')
      return
    }

    setApplyingDashboardAccess(true)
    try {
      for (const entry of entries) {
        const payload: UserDashboardAccessExceptionCreatePayload = {
          datalens_entry_id: entry.datalens_entry_id,
          entry_type: 'dash',
          entry_title: entry.entry_title ?? undefined,
          note: values.note,
        }
        await createException(selectedUserId, payload)
      }
      await queryClient.invalidateQueries({ queryKey: ['user-dashboard-access', selectedUserId] })
      message.success(
        exceptionMode === 'grant'
          ? `Добавлено персональных разрешений: ${entries.length}`
          : `Добавлено персональных ограничений: ${entries.length}`
      )
      setExceptionModalOpen(false)
      exceptionForm.resetFields()
      setSelectedExceptionEntries([])
    } catch (error: unknown) {
      message.error(
        getApiErrorDetail(error) ||
          (exceptionMode === 'grant'
            ? 'Ошибка добавления персонального разрешения'
            : 'Ошибка добавления персонального ограничения')
      )
    } finally {
      setApplyingDashboardAccess(false)
    }
  }

  const handleDeleteException = async (exceptionId: string, mode: 'grant' | 'deny') => {
    if (!selectedUserId) {
      return
    }

    if (mode === 'grant') {
      await deleteGrantMutation.mutateAsync({ userId: selectedUserId, exceptionId })
      return
    }
    await deleteDenyMutation.mutateAsync({ userId: selectedUserId, exceptionId })
  }

  const handleKeepOnlyDashboard = async (keepEntry: { datalens_entry_id: string; entry_title: string | null; label: string }) => {
    if (!selectedUserId) {
      return
    }

    const denyTargets = effectiveDashboardEntries.filter(
      (entry) => entry.datalens_entry_id !== keepEntry.datalens_entry_id
    )
    const targetIds = new Set(denyTargets.map((entry) => entry.datalens_entry_id))
    const grantsToRemove = directGrantExceptions.filter((exception) =>
      targetIds.has(exception.datalens_entry_id)
    )
    const keepDeniesToRemove = directDenyExceptions.filter(
      (exception) => exception.datalens_entry_id === keepEntry.datalens_entry_id
    )
    const deniesToCreate = denyTargets.filter(
      (entry) => !directDenyIds.has(entry.datalens_entry_id)
    )

    setApplyingDashboardAccess(true)
    try {
      for (const exception of keepDeniesToRemove) {
        await usersApi.deleteUserDashboardDeny(selectedUserId, exception.id)
      }
      for (const exception of grantsToRemove) {
        await usersApi.deleteUserDashboardGrant(selectedUserId, exception.id)
      }
      for (const entry of deniesToCreate) {
        await usersApi.createUserDashboardDeny(selectedUserId, {
          datalens_entry_id: entry.datalens_entry_id,
          entry_type: entry.entry_type || 'dash',
          entry_title: entry.entry_title || entry.label,
          note: `Оставлен только дашборд: ${keepEntry.label}`,
        })
      }

      await queryClient.invalidateQueries({ queryKey: ['user-dashboard-access', selectedUserId] })
      message.success(`Оставлен один дашборд, добавлено ограничений: ${deniesToCreate.length}`)
    } catch (error: unknown) {
      message.error(getApiErrorDetail(error) || 'Ошибка массового ограничения дашбордов')
    } finally {
      setApplyingDashboardAccess(false)
    }
  }

  const workspaceTabs: TabsProps['items'] = selectedUser
    ? [
        {
          key: 'overview',
          label: 'Обзор',
          children: (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Alert
                type="info"
                showIcon
                message="Доступ к объектам BI и доступ к данным разведены намеренно"
                description="Группы определяют, какие коллекции, воркбуки и дашборды пользователь видит в BI. RLS-привязки определяют, какие строки данных он увидит внутри уже открытого дашборда."
              />

              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Statistic
                      title="Группы доступа"
                      value={currentGroupSummary.length}
                      prefix={<LockOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Statistic
                      title="Персональные RLS-привязки"
                      value={userRlsBindings?.length ?? 0}
                      prefix={<SafetyOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Statistic
                      title="Эффективные строки RLS"
                      value={effectiveRows?.length ?? 0}
                      prefix={<AppstoreOutlined />}
                    />
                  </Card>
                </Col>
              </Row>

              <Card size="small" title="Карточка сотрудника">
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Email">{selectedUser.email}</Descriptions.Item>
                  <Descriptions.Item label="Роль">
                    <Tag color={ROLE_COLORS[selectedUser.role] || 'default'}>
                      {ROLE_LABELS[selectedUser.role] || selectedUser.role}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Организация">
                    {selectedUser.org_short_name || selectedUser.org_name || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Должность">
                    {selectedUser.position_title || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Статус">
                    <Badge
                      status={selectedUser.is_active ? 'success' : 'default'}
                      text={selectedUser.is_active ? 'Активен' : 'Отключен'}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Legacy userid">
                    {selectedUser.legacy_userid ? <Text code>{selectedUser.legacy_userid}</Text> : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Последний вход" span={2}>
                    {formatLastLogin(selectedUser.last_login)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Телефон">
                    {selectedUser.phone || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Telegram">
                    {selectedUser.telegram_id || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Все должности" span={2}>
                    {selectedUser.positions.length > 0 ? (
                      <Space size={[8, 8]} wrap>
                        {selectedUser.positions.map((position) => (
                          <Tag key={position}>{position}</Tag>
                        ))}
                      </Space>
                    ) : (
                      '—'
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Источник RLS" span={2}>
                    {selectedUser.rls_source_file ? (
                      <Space size={[8, 8]} wrap>
                        <Text>{selectedUser.rls_source_file}</Text>
                        {selectedUser.rls_source_sheet ? <Tag>{selectedUser.rls_source_sheet}</Tag> : null}
                        {selectedUser.rls_source_updated_at ? (
                          <Text type="secondary">
                            {dayjs(selectedUser.rls_source_updated_at).format('DD.MM.YYYY HH:mm')}
                          </Text>
                        ) : null}
                      </Space>
                    ) : (
                      '—'
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <Card size="small" title="Сейчас назначено на уровне объектов BI">
                {currentGroupSummary.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет назначенных групп" />
                ) : (
                  <Space size={[8, 8]} wrap>
                    {currentGroupSummary.map((group) => (
                      <Tooltip
                        key={group.id}
                        title={
                          group.description
                            ? `${group.name} — ${group.description}`
                            : `${group.name} — ${group.zitadel_role_key}`
                        }
                      >
                        <Tag style={{ marginInlineEnd: 0 }}>
                          {group.name}
                        </Tag>
                      </Tooltip>
                    ))}
                  </Space>
                )}
              </Card>
            </Space>
          ),
        },
        {
          key: 'object-access',
          label: 'Объекты BI',
          children: (
            <Spin spinning={accessMutationBusy} tip="Применяем изменения доступа...">
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Alert
                type="info"
                showIcon
                message="Эта вкладка управляет навигационным доступом к BI"
                description="Здесь меняется состав групп доступа пользователя. Сами группы, их дашборды и публикация access-config остаются в классическом разделе «Группы доступа к дашбордам»."
              />

              <Card
                size="small"
                title="Назначенные группы"
                extra={
                  <Space.Compact>
                    <Select
                      showSearch
                      allowClear
                      placeholder="Добавить в группу..."
                      value={addGroupId}
                      options={availableGroups.map((group) => ({
                        value: group.id,
                        label: `${group.name} · ${group.zitadel_role_key}`,
                      }))}
                      style={{ width: 320 }}
                      loading={allGroupsLoading}
                      onChange={(value) => setAddGroupId(value)}
                      filterOption={(input, option) =>
                        (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
                      }
                    />
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      disabled={!selectedUser || !addGroupId || accessMutationBusy}
                      loading={addMemberMutation.isPending}
                      onClick={handleAddGroup}
                    >
                      Добавить
                    </Button>
                  </Space.Compact>
                }
              >
                <List<GroupSummary & { accessMode: { label: string; color: string }; scope: { label: string; color: string } }>
                  loading={userGroupsLoading}
                  locale={{ emptyText: 'Группы не назначены' }}
                  dataSource={currentGroupSummary}
                  renderItem={(group) => (
                    <List.Item
                      actions={[
                        <Popconfirm
                          key="remove"
                          title="Убрать пользователя из группы?"
                          description="Состав группы изменится сразу."
                          okText="Убрать"
                          cancelText="Отмена"
                          onConfirm={() => handleRemoveGroup(group.id)}
                        >
                          <Button
                            type="text"
                            danger
                            disabled={accessMutationBusy}
                            loading={removeMemberMutation.isPending}
                          >
                            Убрать
                          </Button>
                        </Popconfirm>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<TeamOutlined />} />}
                        title={
                          <Space size={[8, 8]} wrap>
                            <Text strong>{group.name}</Text>
                            <Tag color={group.scope.color}>{group.scope.label}</Tag>
                            <Tag color={group.accessMode.color}>{group.accessMode.label}</Tag>
                          </Space>
                        }
                        description={
                          <Space size={[8, 8]} wrap>
                            <Text code>{group.zitadel_role_key}</Text>
                            {group.description ? (
                              <Text type="secondary">{group.description}</Text>
                            ) : null}
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>

              <Card size="small" title="Унаследованные дашборды и итоговая видимость">
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Input
                    allowClear
                    prefix={<SearchOutlined />}
                    placeholder="Поиск по дашбордам"
                    value={dashboardSearch}
                    onChange={(event) => setDashboardSearch(event.target.value)}
                  />
                  <Select
                    allowClear
                    showSearch
                    placeholder="Сравнить с другим пользователем"
                    value={compareUserId}
                    options={compareUserOptions}
                    onChange={(value) => setCompareUserId(value ?? null)}
                    filterOption={(input, option) =>
                      (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
                    }
                  />
                  <Alert
                    type="info"
                    showIcon
                    message="Персональные разрешения и ограничения уже применяются в BI"
                    description="Список ниже показывает базовый унаследованный набор от групп. Теги «итог: виден/скрыт» показывают результат после персональных исключений. В BI пользователь увидит именно итоговый набор."
                  />
                  {directExceptionSummary && directExceptionSummary.total_exceptions > 0 ? (
                    <Card
                      size="small"
                      type="inner"
                      title="Персональные исключения"
                      extra={
                        <Space wrap>
                          <Button size="small" disabled={accessMutationBusy} onClick={() => openExceptionModal('grant')}>
                            Разрешить
                          </Button>
                          <Button size="small" danger disabled={accessMutationBusy} onClick={() => openExceptionModal('deny')}>
                            Скрыть
                          </Button>
                        </Space>
                      }
                    >
                      <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Space size={[8, 8]} wrap>
                          <Tag color="cyan">персонально открыто {directExceptionSummary.direct_grants}</Tag>
                          <Tag color="red">персонально скрыто {directExceptionSummary.direct_denies}</Tag>
                          <Tag color="gold">перекрывают группы {directExceptionSummary.overlapping_exceptions}</Tag>
                          <Tag color="purple">конфликты {directExceptionSummary.conflicting_entries}</Tag>
                        </Space>
                        {projectionSummary ? (
                          <Space size={[8, 8]} wrap>
                            <Tag color="blue">до исключений видно {projectionSummary.current_visible_entries}</Tag>
                            <Tag color="green">после исключений видно {projectionSummary.projected_visible_entries}</Tag>
                            <Tag color="green">будет добавлено {projectionSummary.would_add_visible}</Tag>
                            <Tag color="volcano">будет скрыто {projectionSummary.would_remove_visible}</Tag>
                            <Tag color="blue">лишние разрешения {projectionSummary.redundant_grants}</Tag>
                            <Tag>лишние ограничения {projectionSummary.redundant_denies}</Tag>
                          </Space>
                        ) : null}
                        {projectionSummary && (
                          projectionSummary.would_add_visible > 0 ||
                          projectionSummary.would_remove_visible > 0 ||
                          projectionSummary.conflicting_entries > 0
                        ) ? (
                          <Alert
                            type="info"
                            showIcon
                            message="Итоговая видимость после персональных исключений"
                            description="Здесь показана разница между унаследованным набором от групп и фактическим BI-набором после персональных разрешений и ограничений. Конфликт нужно снять вручную."
                          />
                        ) : null}
                        <List
                          size="small"
                          dataSource={directExceptions}
                          renderItem={(exception) => (
                            <List.Item
                              actions={[
                                <Popconfirm
                                  key="delete"
                                  title={
                                    exception.exception_type === 'grant'
                                      ? 'Удалить персональное разрешение?'
                                      : 'Удалить персональное ограничение?'
                                  }
                                  onConfirm={() =>
                                    handleDeleteException(
                                      exception.id,
                                      exception.exception_type as 'grant' | 'deny'
                                    )
                                  }
                                >
                                  <Button
                                    type="text"
                                    danger
                                    loading={
                                      deleteGrantMutation.isPending || deleteDenyMutation.isPending
                                    }
                                    disabled={accessMutationBusy}
                                  >
                                    Удалить
                                  </Button>
                                </Popconfirm>,
                              ]}
                            >
                              <List.Item.Meta
                                avatar={<Avatar shape="square" icon={<LockOutlined />} />}
                                title={
                                  <Space size={[8, 8]} wrap>
                                    <Text strong>{exception.label}</Text>
                                    <Tag>{exception.entry_type}</Tag>
                                    <Tag color={getExceptionTypeMeta(exception.exception_type).color}>
                                      {getExceptionTypeMeta(exception.exception_type).label}
                                    </Tag>
                                    <Tag color={getProjectedEffectMeta(exception.projected_effect).color}>
                                      {getProjectedEffectMeta(exception.projected_effect).label}
                                    </Tag>
                                    {exception.overlaps_inherited ? (
                                      <Tag color="gold">перекрывает группу</Tag>
                                    ) : (
                                      <Tag>только исключение</Tag>
                                    )}
                                    {exception.conflicts_with_other_exception ? (
                                      <Tag color="purple">конфликт разрешения и ограничения</Tag>
                                    ) : null}
                                  </Space>
                                }
                                description={
                                  <Space direction="vertical" size={4}>
                                    <Space size={[8, 8]} wrap>
                                      <Text code>{exception.datalens_entry_id}</Text>
                                      <Text type="secondary">
                                        создано {dayjs(exception.created_at).format('DD.MM.YYYY HH:mm')}
                                      </Text>
                                    </Space>
                                    <Text type="secondary">{exception.projected_note}</Text>
                                    {exception.note ? <Text type="secondary">{exception.note}</Text> : null}
                                  </Space>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      </Space>
                    </Card>
                  ) : (
                    <Card
                      size="small"
                      type="inner"
                      title="Персональные исключения"
                      extra={
                        <Space wrap>
                          <Button size="small" disabled={accessMutationBusy} onClick={() => openExceptionModal('grant')}>
                            Разрешить
                          </Button>
                          <Button size="small" danger disabled={accessMutationBusy} onClick={() => openExceptionModal('deny')}>
                            Скрыть
                          </Button>
                        </Space>
                      }
                    >
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Персональных разрешений и ограничений пока нет"
                      />
                    </Card>
                  )}
                {dashboardAccessSummary && !dashboardAccess?.has_wildcard ? (
                  <Space size={[8, 8]} wrap>
                    <Tag color="green">одна группа {dashboardAccessSummary.single_group}</Tag>
                    <Tag color="orange">несколько групп {dashboardAccessSummary.multi_group_same_org}</Tag>
                    <Tag color="volcano">разные ЛПУ {dashboardAccessSummary.multi_group_cross_org}</Tag>
                    <Tag color="green">низкий риск {dashboardAccessSummary.override_risk_low}</Tag>
                    <Tag color="orange">средний риск {dashboardAccessSummary.override_risk_medium}</Tag>
                    <Tag color="volcano">высокий риск {dashboardAccessSummary.override_risk_high}</Tag>
                  </Space>
                ) : null}
                {dashboardAccessSummary && dashboardAccessSummary.override_risk_high > 0 ? (
                  <Alert
                    type="warning"
                    showIcon
                    message="Есть BI-объекты с высоким риском для персональных исключений"
                    description="Часть дашбордов унаследована сразу из нескольких групп с разными ЛПУ-контекстами. Перед персональным разрешением или ограничением здесь нужна отдельная проверка конфликта."
                  />
                ) : null}
                {dashboardAccessLoading ? (
                  <List size="small" loading dataSource={[]} />
                ) : dashboardAccess?.has_wildcard ? (
                  <Alert
                    type="warning"
                    showIcon
                    message="У пользователя полный доступ к объектам BI"
                    description="Текущий доступ к BI-объектам не ограничен группами доступа, потому что для этой учетной записи действует полный доступ на уровне платформенной роли."
                  />
                ) : filteredEffectiveDashboardEntries.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Эффективный набор дашбордов пуст" />
                ) : (
                  <List
                    size="small"
                    loading={dashboardAccessLoading}
                    dataSource={filteredEffectiveDashboardEntries}
                    renderItem={(entry) => (
                      <List.Item
                        actions={[
                          <Popconfirm
                            key="keep-only"
                            title="Оставить только этот дашборд?"
                            description="Для остальных дашбордов из текущего унаследованного набора будут созданы персональные ограничения."
                            okText="Оставить только этот"
                            cancelText="Отмена"
                            onConfirm={() => handleKeepOnlyDashboard(entry)}
                            disabled={accessMutationBusy || effectiveDashboardEntries.length <= 1}
                          >
                            <Button
                              size="small"
                              disabled={accessMutationBusy || effectiveDashboardEntries.length <= 1}
                            >
                              Оставить только этот
                            </Button>
                          </Popconfirm>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<Avatar shape="square" icon={<AppstoreOutlined />} />}
                          title={
                            <Space size={[8, 8]} wrap>
                              <Text strong>{entry.label}</Text>
                              <Tag>{entry.entry_type}</Tag>
                              <Tag color={getInheritedScopeMeta(entry.inherited_scope).color}>
                                {getInheritedScopeMeta(entry.inherited_scope).label}
                              </Tag>
                              <Tag color={getOverrideRiskMeta(entry.override_risk).color}>
                                {getOverrideRiskMeta(entry.override_risk).label}
                              </Tag>
                              <Tag color={getProjectedVisibilityMeta(entry.projected_visibility).color}>
                                {getProjectedVisibilityMeta(entry.projected_visibility).label}
                              </Tag>
                              {(directExceptionsByEntryId.get(entry.datalens_entry_id) || []).map((exception) => (
                                <Tag
                                  key={`${entry.datalens_entry_id}-${exception.id}`}
                                  color={getExceptionTypeMeta(exception.exception_type).color}
                                >
                                  {getExceptionTypeMeta(exception.exception_type).label}
                                </Tag>
                              ))}
                            </Space>
                          }
                          description={
                            <Space direction="vertical" size={4}>
                              <Space size={[8, 8]} wrap>
                                <Text code>{entry.datalens_entry_id}</Text>
                                <Text type="secondary">групп-источников: {entry.source_group_count}</Text>
                                {entry.source_groups.map((group) => (
                                  <Tag key={`${entry.datalens_entry_id}-${group.id}`}>{group.name}</Tag>
                                ))}
                              </Space>
                              <Text type="secondary">{entry.override_note}</Text>
                              {entry.projected_note ? <Text type="secondary">{entry.projected_note}</Text> : null}
                            </Space>
                          }
                        />
                      </List.Item>
                    )}
                  />
                )}
                {compareUser ? (
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Card size="small" title={`Есть у ${selectedUser?.full_name}, нет у ${compareUser.full_name}`}>
                        {compareDashboardAccess?.has_wildcard ? (
                          <Alert
                            type="info"
                            showIcon
                            message={`${compareUser.full_name} имеет полный wildcard-доступ`}
                            description="Поштучное сравнение с этой учетной записью недоступно, потому что второй пользователь видит все объекты BI."
                          />
                        ) : dashboardAccess?.has_wildcard ? (
                          <Alert
                            type="info"
                            showIcon
                            message={`${selectedUser?.full_name} имеет полный wildcard-доступ`}
                            description="Поштучное сравнение с этой учетной записью недоступно, потому что выбранный пользователь видит все объекты BI."
                          />
                        ) : dashboardAccessDiff.selectedOnly.length === 0 ? (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет отличий" />
                        ) : (
                          <List
                            size="small"
                            loading={compareDashboardAccessLoading}
                            dataSource={dashboardAccessDiff.selectedOnly}
                            renderItem={(entry) => (
                              <List.Item>
                                <Space direction="vertical" size={0}>
                                  <Text strong>{entry.label}</Text>
                                  <Text type="secondary" code>{entry.datalens_entry_id}</Text>
                                </Space>
                              </List.Item>
                            )}
                          />
                        )}
                      </Card>
                    </Col>
                    <Col xs={24} md={12}>
                      <Card size="small" title={`Есть у ${compareUser.full_name}, нет у ${selectedUser?.full_name}`}>
                        {compareDashboardAccess?.has_wildcard ? (
                          <Alert
                            type="info"
                            showIcon
                            message={`${compareUser.full_name} имеет полный wildcard-доступ`}
                            description="Поштучное сравнение с этой учетной записью недоступно, потому что второй пользователь видит все объекты BI."
                          />
                        ) : dashboardAccess?.has_wildcard ? (
                          <Alert
                            type="info"
                            showIcon
                            message={`${selectedUser?.full_name} имеет полный wildcard-доступ`}
                            description="Поштучное сравнение с этой учетной записью недоступно, потому что выбранный пользователь видит все объекты BI."
                          />
                        ) : dashboardAccessDiff.compareOnly.length === 0 ? (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет отличий" />
                        ) : (
                          <List
                            size="small"
                            loading={compareDashboardAccessLoading}
                            dataSource={dashboardAccessDiff.compareOnly}
                            renderItem={(entry) => (
                              <List.Item>
                                <Space direction="vertical" size={0}>
                                  <Text strong>{entry.label}</Text>
                                  <Text type="secondary" code>{entry.datalens_entry_id}</Text>
                                </Space>
                              </List.Item>
                            )}
                          />
                        )}
                      </Card>
                    </Col>
                  </Row>
                ) : null}
                </Space>
              </Card>
            </Space>
            </Spin>
          ),
        },
        {
          key: 'identity',
          label: 'Логин / identity',
          children: (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Card size="small">
                <UserIdentityTab userId={selectedUser.id} canonicalEmail={selectedUser.email} zitadelId={selectedUser.zitadel_id} />
              </Card>
            </Space>
          ),
        },
        {
          key: 'data-access',
          label: 'RLS / данные',
          children: (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Alert
                type="info"
                showIcon
                message="Эта вкладка управляет именно видимостью данных внутри дашбордов"
                description="Изменения сохраняются в слое RLS. Группы и видимость BI здесь не затрагиваются."
              />
              <Card size="small">
                <UserRlsTab userId={selectedUser.id} lpuCode={selectedUser.org_code} />
              </Card>
            </Space>
          ),
        },
      ]
    : []

  return (
    <div>
      <Breadcrumb
        items={[
          { title: 'Администрирование' },
          { title: 'Рабочее место доступов' },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col>
          <Space direction="vertical" size={4}>
            <Space align="center">
              <Title level={4} style={{ margin: 0 }}>
                Рабочее место доступов
              </Title>
              <Tag color="processing">Экспериментальный интерфейс</Tag>
            </Space>
            <Paragraph type="secondary" style={{ margin: 0, maxWidth: 860 }}>
              Альтернативный контур по сотруднику для проверки и настройки BI-доступов без раскрывающихся строк.
              Классические разделы остаются рабочими и не заменяются.
            </Paragraph>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button icon={<UserAddOutlined />} onClick={() => setWizardOpen(true)}>
              Создать сотрудника
            </Button>
            <Button onClick={() => navigate('/admin/users')}>
              Классические пользователи
            </Button>
            <Button onClick={() => navigate('/admin/access-groups')}>
              Классические группы
            </Button>
          </Space>
        </Col>
      </Row>

      {workspaceOrgLabel ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Контекст ЛПУ: ${workspaceOrgLabel}`}
          description="Этот экран открыт как глубокая настройка сотрудников, групп и RLS. Вернуться к сводке по выбранному ЛПУ можно без поиска раздела в меню."
          action={
            <Button size="small" onClick={() => navigate(biCenterUrl)}>
              В BI-центр
            </Button>
          }
        />
      ) : null}

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} xl={8}>
          <Card
            title="Сотрудники"
            size="small"
            style={{ height: '100%' }}
            extra={<Text type="secondary">{usersData?.total ?? 0}</Text>}
          >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Поиск по ФИО или email"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />

              <Row gutter={8}>
                {isSuperadmin ? (
                  <Col span={24}>
                    <Select
                      allowClear
                      showSearch
                      placeholder="Организация"
                      value={orgFilter}
                      options={orgOptions}
                      style={{ width: '100%' }}
                      onChange={(value) => setOrgFilter(value)}
                      filterOption={(input, option) =>
                        (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
                      }
                    />
                  </Col>
                ) : null}
                <Col span={12}>
                  <Select
                    allowClear
                    placeholder="Роль"
                    value={roleFilter}
                    style={{ width: '100%' }}
                    options={[
                      { value: 'superadmin', label: ROLE_LABELS.superadmin },
                      { value: 'org_admin', label: ROLE_LABELS.org_admin },
                      { value: 'analyst', label: ROLE_LABELS.analyst },
                    ]}
                    onChange={(value) => setRoleFilter(value)}
                  />
                </Col>
                <Col span={12}>
                  <Select
                    allowClear
                    placeholder="Статус"
                    value={statusFilter}
                    style={{ width: '100%' }}
                    onChange={(value) => setStatusFilter(value)}
                    options={[
                      { value: true, label: 'Активные' },
                      { value: false, label: 'Отключенные' },
                    ]}
                  />
                </Col>
              </Row>

              <div
                style={{
                  maxHeight: 760,
                  overflowY: 'auto',
                  paddingRight: 4,
                }}
              >
                <List
                  loading={usersLoading}
                  locale={{ emptyText: 'Сотрудники не найдены' }}
                  dataSource={users}
                  renderItem={(user) => {
                    const selected = user.id === selectedUserId
                    return (
                      <List.Item
                        onClick={() => setSelectedUserId(user.id)}
                        style={{
                          cursor: 'pointer',
                          marginBottom: 8,
                          padding: 14,
                          borderRadius: 12,
                          border: `1px solid ${selected ? designToken.colorPrimary : designToken.colorBorderSecondary}`,
                          background: selected ? designToken.colorPrimaryBg : designToken.colorBgLayout,
                        }}
                      >
                        <List.Item.Meta
                          avatar={<Avatar icon={<UserOutlined />} />}
                          title={
                            <Space size={[8, 8]} wrap>
                              <Text strong>{user.full_name}</Text>
                              <Tag color={ROLE_COLORS[user.role] || 'default'}>
                                {ROLE_LABELS[user.role] || user.role}
                              </Tag>
                              {selected ? <Tag color="processing">Выбран</Tag> : null}
                            </Space>
                          }
                          description={
                            <Space direction="vertical" size={2}>
                              <Text type="secondary">{user.email}</Text>
                              <Space size={[8, 4]} wrap>
                                <Badge
                                  status={user.is_active ? 'success' : 'default'}
                                  text={user.is_active ? 'Активен' : 'Отключен'}
                                />
                                <Text type="secondary">
                                  {user.org_short_name || user.org_name || 'Без организации'}
                                </Text>
                                {user.position_title ? (
                                  <Text type="secondary">{user.position_title}</Text>
                                ) : null}
                              </Space>
                            </Space>
                          }
                        />
                      </List.Item>
                    )
                  }}
                />
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} xl={16}>
          {selectedUser ? (
            <Card
              size="small"
              style={{ height: '100%' }}
              title={
                <Space size={12}>
                  <Avatar icon={<UserOutlined />} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{selectedUser.full_name}</div>
                    <Space size={[8, 4]} wrap>
                      <Text type="secondary">{selectedUser.email}</Text>
                      {selectedUser.position_title ? <Tag>{selectedUser.position_title}</Tag> : null}
                    </Space>
                  </div>
                </Space>
              }
              extra={
                <Space size={[8, 8]} wrap>
                  <Tag color={ROLE_COLORS[selectedUser.role] || 'default'}>
                    {ROLE_LABELS[selectedUser.role] || selectedUser.role}
                  </Tag>
                  <Tag>{selectedUser.org_short_name || selectedUser.org_name || 'Без организации'}</Tag>
                </Space>
              }
            >
              <Tabs items={workspaceTabs} />
            </Card>
          ) : (
            <Card size="small" style={{ height: '100%' }}>
              <Empty description="Выберите сотрудника слева" />
            </Card>
          )}
        </Col>
      </Row>

      <Modal
        title={exceptionMode === 'grant' ? 'Добавить персональное разрешение' : 'Добавить персональное ограничение'}
        open={exceptionModalOpen}
        onCancel={() => {
          setExceptionModalOpen(false)
          exceptionForm.resetFields()
          setSelectedExceptionEntries([])
        }}
        onOk={handleCreateException}
        confirmLoading={applyingDashboardAccess}
        okButtonProps={{ disabled: selectedExceptionEntries.length === 0 }}
        okText={
          exceptionMode === 'grant'
            ? `Разрешить${selectedExceptionEntries.length ? ` (${selectedExceptionEntries.length})` : ''}`
            : `Скрыть${selectedExceptionEntries.length ? ` (${selectedExceptionEntries.length})` : ''}`
        }
        cancelText="Отмена"
      >
        <Form
          form={exceptionForm}
          layout="vertical"
        >
          <Form.Item
            label="BI-объекты"
            required
            extra="Выберите один или несколько дашбордов. Уже существующие исключения подсвечены и недоступны для повторного выбора."
          >
            <DatalensEntryPicker
              mode="multiple"
              values={selectedExceptionEntryIds}
              onChangeMany={setSelectedExceptionEntries}
              orgId={selectedUser?.org_id}
              disabled={applyingDashboardAccess}
              disabledEntryIds={disabledExceptionEntryIds}
              markersByEntryId={exceptionMarkersByEntryId}
            />
          </Form.Item>
          <Form.Item name="note" label="Заметка">
            <Input.TextArea rows={3} placeholder="Зачем нужно это персональное разрешение или ограничение" />
          </Form.Item>
        </Form>
      </Modal>

      <CreateUserWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </div>
  )
}
